import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";

// Point pdf.js worker to a CDN matching the installed version.
pdfjsLib.GlobalWorkerOptions.workerSrc =
    "https://unpkg.com/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js";

/**
 * PdfViewer renders all pages of a PDF (from a URL) into a vertically
 * scrolling stack. Used by the side-by-side comparison view.
 *
 * Props:
 *   url:        URL of the PDF to render
 *   scale:      number (default 1.4)
 *   onScroll:   (scrollRatio) => void (for synchronized scrolling)
 *   syncRatio:  number — set scrollTop to ratio*scrollHeight
 *   label:      string label shown at the top of the viewer
 *   testId:     data-testid
 */
const PdfViewer = React.forwardRef(function PdfViewer(
    { url, scale = 1.4, onScroll, syncRatio, label, testId, accent = "#ffffff" },
    ref,
) {
    const containerRef = useRef(null);
    const innerRef = useRef(null);
    const [pages, setPages] = useState(0);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    // Expose container ref to parent
    React.useImperativeHandle(ref, () => containerRef.current);

    useEffect(() => {
        let cancelled = false;
        let observer = null;
        async function render() {
            if (!url || !innerRef.current) return;
            setLoading(true);
            setError(null);
            innerRef.current.innerHTML = "";

            try {
                const loadingTask = pdfjsLib.getDocument({ url, withCredentials: false });
                const pdf = await loadingTask.promise;
                if (cancelled) return;
                setPages(pdf.numPages);

                const pageJobs = [];
                for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                    const page = await pdf.getPage(pageNum);
                    if (cancelled) return;
                    const viewport = page.getViewport({ scale });

                    const wrapper = document.createElement("div");
                    wrapper.style.width = `${viewport.width}px`;
                    wrapper.style.height = `${viewport.height}px`;
                    wrapper.style.margin = "0 auto 18px auto";
                    wrapper.style.background = "#18181b";
                    wrapper.style.borderRadius = "6px";
                    wrapper.style.display = "grid";
                    wrapper.style.placeItems = "center";
                    wrapper.style.color = "#71717a";
                    wrapper.style.font = "11px monospace";
                    wrapper.textContent = `Page ${pageNum}`;

                    const canvas = document.createElement("canvas");
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    canvas.style.display = "block";
                    canvas.style.background = "#fff";
                    canvas.style.borderRadius = "6px";
                    canvas.style.boxShadow =
                        "0 1px 0 rgba(255,255,255,0.04), 0 24px 60px rgba(0,0,0,0.35)";

                    innerRef.current.appendChild(wrapper);
                    pageJobs.push({ wrapper, canvas, page, viewport, rendered: false });
                }

                const renderPage = async (job) => {
                    if (job.rendered || cancelled) return;
                    job.rendered = true;
                    job.wrapper.textContent = "";
                    job.wrapper.appendChild(job.canvas);
                    const ctx = job.canvas.getContext("2d");
                    await job.page.render({ canvasContext: ctx, viewport: job.viewport }).promise;
                };

                observer = new IntersectionObserver(
                    (entries) => {
                        entries.forEach((entry) => {
                            if (!entry.isIntersecting) return;
                            const job = pageJobs.find((item) => item.wrapper === entry.target);
                            if (job) {
                                renderPage(job);
                                observer.unobserve(entry.target);
                            }
                        });
                    },
                    {
                        root: containerRef.current,
                        rootMargin: "900px 0px",
                        threshold: 0.01,
                    },
                );

                pageJobs.forEach((job) => observer.observe(job.wrapper));
                pageJobs.slice(0, 2).forEach((job) => renderPage(job));
                setLoading(false);
            } catch (e) {
                console.error("PDF render failed:", e);
                setError(e.message || "Failed to render PDF");
                setLoading(false);
            }
        }
        render();
        return () => {
            cancelled = true;
            if (observer) observer.disconnect();
        };
    }, [url, scale]);

    // Sync scroll from sibling
    useEffect(() => {
        if (syncRatio == null || !containerRef.current) return;
        const el = containerRef.current;
        const target = syncRatio * (el.scrollHeight - el.clientHeight);
        if (Math.abs(el.scrollTop - target) > 4) {
            el.scrollTop = target;
        }
    }, [syncRatio]);

    function handleScroll(e) {
        if (!onScroll) return;
        const el = e.currentTarget;
        const denom = el.scrollHeight - el.clientHeight;
        const ratio = denom > 0 ? el.scrollTop / denom : 0;
        onScroll(ratio);
    }

    return (
        <div
            className="flex flex-col h-full overflow-hidden"
            data-testid={testId}
        >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/40">
                <div className="flex items-center gap-2">
                    <div
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ background: accent }}
                    />
                    <span className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-400">
                        {label}
                    </span>
                </div>
                <span className="text-[10px] font-mono text-zinc-500">
                    {pages > 0 ? `${pages} page${pages > 1 ? "s" : ""}` : ""}
                </span>
            </div>
            <div
                ref={containerRef}
                onScroll={handleScroll}
                className="flex-1 overflow-auto bg-zinc-950 p-4"
            >
                {loading && (
                    <div className="text-center text-xs font-mono text-zinc-500 py-10">
                        Rendering PDF…
                    </div>
                )}
                {error && (
                    <div className="text-center text-xs font-mono text-red-400 py-10">
                        {error}
                    </div>
                )}
                <div ref={innerRef} />
            </div>
        </div>
    );
});

export default PdfViewer;
